import React, { FC, useState } from 'react';
import { useConfiguration } from '../../context/ConfigurationContext';
import { PLANS } from '../../constants';
import QRCode from '../../components/ui/QRCode';
import { API_BASE_URL } from '../../config/apiUrl';

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

const PROFILE_LABELS: Record<string, { title: string; icon: string; color: string }> = {
  cognitive: { title: 'Memory & Confusion', icon: 'psychology_alt', color: 'text-orange-500' },
  physical: { title: 'Physical', icon: 'touch_app', color: 'text-green-500' },
  visual: { title: 'Visual', icon: 'visibility', color: 'text-blue-500' },
  custom: { title: 'Custom', icon: 'tune', color: 'text-gray-500' },
};

interface ConfigurationCompleteProps {
  onFinish: () => void;
  onStartOver: () => void;
}

const ConfigurationComplete: FC<ConfigurationCompleteProps> = ({ onFinish, onStartOver }) => {
  const {
    selectedProfile,
    severityLevel,
    selectedPlan,
    contacts,
    activationCode: savedActivationCode,
  } = useConfiguration();

  const [copied, setCopied] = useState(false);
  const [checkoutEmail, setCheckoutEmail] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [showActivation, setShowActivation] = useState(!STRIPE_PUBLISHABLE_KEY);

  const handleStartTrial = async () => {
    if (!checkoutEmail) return;
    setCheckoutLoading(true);
    setCheckoutError('');
    try {
      const response = await fetch(`${API_BASE_URL}/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planTier: 'plus',
          caregiverEmail: checkoutEmail,
        }),
      });
      if (!response.ok) throw new Error('Failed to create checkout session');
      const { url } = await response.json();
      if (url) window.location.href = url;
    } catch (err) {
      setCheckoutError('Could not connect to payment. Please try again.');
      console.error('Stripe checkout error:', err);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const activationCode = savedActivationCode
    ? `${savedActivationCode.slice(0, 3)}-${savedActivationCode.slice(3)}`
    : 'LOADING...';

  const qrCodeUrl = savedActivationCode
    ? `${API_BASE_URL}/config/activate/${savedActivationCode}`
    : '';

  const plan = PLANS.find(p => p.id === selectedPlan);
  const profile = selectedProfile ? PROFILE_LABELS[selectedProfile] : null;

  const handleCopyCode = () => {
    navigator.clipboard.writeText(activationCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-brand-bg dark:bg-slate-900 font-body pb-24">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-brand-border dark:border-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-center">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-brand-primary">check_circle</span>
              <span className="font-bold text-brand-text dark:text-white">Configuration Complete</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Icon */}
        <div className="text-center mb-12">
          <div className="w-24 h-24 rounded-full bg-brand-primary-light flex items-center justify-center mx-auto mb-6">
            <span className="material-symbols-outlined text-5xl text-brand-primary">celebration</span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-display font-bold text-brand-text dark:text-white mb-4">
            You're all set!
          </h1>
          <p className="text-lg text-brand-text-light dark:text-slate-400">
            The phone is now configured and ready for your loved one.
          </p>
        </div>

        {/* Configuration Summary */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-brand-border dark:border-slate-700 overflow-hidden mb-8">
          <div className="p-4 border-b border-brand-border dark:border-slate-700">
            <h2 className="font-bold text-brand-text dark:text-white">Configuration Summary</h2>
          </div>

          <div className="divide-y divide-brand-border dark:divide-slate-700">
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className={`material-symbols-outlined ${profile?.color || 'text-brand-text-muted'}`}>
                  {profile?.icon || 'person'}
                </span>
                <span className="text-brand-text-light dark:text-slate-400">Profile</span>
              </div>
              <span className="font-bold text-brand-text dark:text-white">
                {profile?.title || 'Not set'}
              </span>
            </div>

            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="material-symbols-outlined text-brand-text-muted">speed</span>
                <span className="text-brand-text-light dark:text-slate-400">Severity Level</span>
              </div>
              <span className="font-bold text-brand-text dark:text-white capitalize">
                {severityLevel || 'Not set'}
              </span>
            </div>

            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="material-symbols-outlined text-brand-primary">workspace_premium</span>
                <span className="text-brand-text-light dark:text-slate-400">Plan</span>
              </div>
              <div className="text-right">
                <span className="font-bold text-brand-text dark:text-white capitalize">
                  {plan?.name || 'Not set'}
                </span>
                {plan && (
                  <p className="text-sm text-brand-text-muted">{plan.priceDisplay}/mo</p>
                )}
              </div>
            </div>

            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="material-symbols-outlined text-brand-text-muted">contacts</span>
                <span className="text-brand-text-light dark:text-slate-400">Contacts</span>
              </div>
              <span className="font-bold text-brand-text dark:text-white">
                {contacts.length} added
              </span>
            </div>
          </div>
        </div>

        {/* STEP 1: Payment — shown first */}
        {STRIPE_PUBLISHABLE_KEY && !showActivation && (
          <div className="bg-white dark:bg-slate-800 rounded-2xl border-2 border-brand-accent/30 p-6 sm:p-8 mb-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-brand-accent/10 flex items-center justify-center mx-auto mb-4">
                <span className="material-symbols-outlined text-3xl text-brand-accent">rocket_launch</span>
              </div>
              <h2 className="text-2xl font-display font-bold text-brand-text dark:text-white">
                Start Your 30-Day Free Trial
              </h2>
              <p className="text-brand-text-muted mt-2">
                No charge today. $10.99 CAD/month after trial. Cancel anytime.
              </p>
            </div>

            <div className="max-w-md mx-auto">
              <label className="block text-sm font-medium text-brand-text dark:text-white mb-2">
                Your email address
              </label>
              <input
                type="email"
                value={checkoutEmail}
                onChange={(e) => setCheckoutEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-3 rounded-xl border-2 border-brand-border dark:border-slate-600 bg-white dark:bg-slate-700 text-brand-text dark:text-white focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 outline-none transition-all mb-4"
              />
              <button
                onClick={handleStartTrial}
                disabled={checkoutLoading || !checkoutEmail}
                className="w-full py-4 bg-brand-accent text-white rounded-xl font-bold text-lg hover:bg-[#c96a3f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_4px_14px_rgba(224,123,76,0.35)]"
              >
                {checkoutLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="material-symbols-outlined animate-spin text-lg">progress_activity</span>
                    Redirecting to checkout...
                  </span>
                ) : (
                  'Start 30-Day Free Trial'
                )}
              </button>
              {checkoutError && (
                <p className="text-sm text-red-500 text-center mt-2">{checkoutError}</p>
              )}
              <button
                onClick={() => setShowActivation(true)}
                className="w-full mt-3 py-2 text-sm text-brand-text-muted hover:text-brand-text transition-colors"
              >
                Skip for now — I'll subscribe later
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Activation code & QR — shown after payment or skip */}
        {showActivation && (
          <>
            {/* QR Code & Activation */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-brand-border dark:border-slate-700 p-6 mb-8">
              <h2 className="text-xl font-bold text-brand-text dark:text-white mb-6 text-center">
                Activate the Phone
              </h2>

              <div className="grid sm:grid-cols-2 gap-8 items-center">
                {/* QR Code */}
                <div className="flex flex-col items-center">
                  <div className="p-4 bg-white rounded-2xl shadow-lg mb-4">
                    {qrCodeUrl ? (
                      <QRCode value={qrCodeUrl} size={180} />
                    ) : (
                      <div className="w-[180px] h-[180px] flex items-center justify-center bg-brand-bg-warm rounded">
                        <span className="text-brand-text-muted">Loading...</span>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-brand-text-muted mb-2">Or enter this code manually:</p>
                  <div className="flex items-center gap-2">
                    <div className="text-3xl font-mono font-bold text-brand-primary tracking-widest">
                      {activationCode}
                    </div>
                    <button
                      onClick={handleCopyCode}
                      className="p-2 text-brand-text-muted hover:text-brand-primary transition-colors"
                      title="Copy code"
                    >
                      <span className="material-symbols-outlined">
                        {copied ? 'check' : 'content_copy'}
                      </span>
                    </button>
                  </div>
                  {copied && (
                    <p className="text-sm text-brand-primary mt-2">Copied to clipboard!</p>
                  )}
                </div>

                {/* Steps */}
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-brand-primary">1</span>
                    </div>
                    <div>
                      <p className="font-medium text-brand-text dark:text-white">Download AidFone</p>
                      <p className="text-sm text-brand-text-muted">Get the app from the Play Store on their phone</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-brand-primary">2</span>
                    </div>
                    <div>
                      <p className="font-medium text-brand-text dark:text-white">Scan or Enter Code</p>
                      <p className="text-sm text-brand-text-muted">Open the app and scan the QR code or type the code</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-brand-primary">3</span>
                    </div>
                    <div>
                      <p className="font-medium text-brand-text dark:text-white">Auto-Configure</p>
                      <p className="text-sm text-brand-text-muted">The phone will sync and apply all settings automatically</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* What Happens Next */}
            <div className="bg-brand-primary-light rounded-2xl p-6 mb-8">
              <h3 className="font-bold text-brand-text dark:text-white mb-4">
                What Happens Next
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-brand-primary flex-shrink-0">schedule</span>
                  <div>
                    <p className="font-medium text-brand-text dark:text-white">Instant Setup</p>
                    <p className="text-sm text-brand-text-muted">Once synced, the phone will be immediately ready to use</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-brand-primary flex-shrink-0">notifications_active</span>
                  <div>
                    <p className="font-medium text-brand-text dark:text-white">You'll Receive Alerts</p>
                    <p className="text-sm text-brand-text-muted">Get notifications for emergencies, low battery, and safety events</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-brand-primary flex-shrink-0">dashboard</span>
                  <div>
                    <p className="font-medium text-brand-text dark:text-white">Monitor from Dashboard</p>
                    <p className="text-sm text-brand-text-muted">Access the caregiver dashboard anytime to check status</p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Legal Disclaimer */}
        <div className="p-4 bg-brand-bg-warm dark:bg-slate-800/50 rounded-xl">
          <p className="text-xs text-brand-text-muted dark:text-slate-400 leading-relaxed">
            <strong>Important:</strong> AidFone is not a medical device. Phone Drop Alert monitors the phone, not the person.
            Emergency services response times vary by location. Features require active internet connection.
            Your first billing cycle will begin after the 30-day free trial period.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 border-t border-brand-border dark:border-slate-800 p-4">
        <div className="max-w-4xl mx-auto flex flex-col-reverse sm:flex-row items-center justify-between gap-3">
          <button
            onClick={onStartOver}
            className="w-full sm:w-auto px-6 py-3 text-brand-text-light dark:text-slate-400 font-medium hover:text-brand-text dark:hover:text-white transition-colors text-center"
          >
            Configure Another Device
          </button>

          {showActivation ? (
            <button
              onClick={onFinish}
              className="w-full sm:w-auto px-8 py-3 bg-brand-accent text-white font-bold rounded-xl hover:bg-[#c96a3f] transition-all shadow-lg shadow-brand-accent/30"
            >
              Go to Dashboard
              <span className="material-symbols-outlined ml-2 align-middle">arrow_forward</span>
            </button>
          ) : (
            <button
              onClick={onFinish}
              className="w-full sm:w-auto px-8 py-3 text-brand-text-light dark:text-slate-400 font-medium hover:text-brand-text dark:hover:text-white transition-colors"
            >
              Skip to Dashboard
              <span className="material-symbols-outlined ml-2 align-middle">arrow_forward</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfigurationComplete;
